import type { Ref, WritableComputedRef } from "vue";
import type { ISwiperConfig } from "~/types/swipe-bet/config";
import type { ISwiperContent } from "~/types/swipe-bet/content";
import { STATUS_CONSTANTS } from "./statusConstants";
import type { ISwiperState } from "./swiperState";
export type TSwiperDecision = "like" | "super" | "down" | "nope";
// Define the emit function type for the swiper component
type SwiperEmitFunction = (event: "submit", payload: { type: TSwiperDecision; key: string | number; item: ISwiperContent }) => void;
const useSwiperCore = (
	swiperState: Ref<ISwiperState>,
	swiperConfig: Ref<ISwiperConfig>,
	emit: SwiperEmitFunction,
	queue: WritableComputedRef<ISwiperContent[]>
) => {
	// Store removed items for rewind functionality
	const removedItems: ISwiperContent[] = [];
	const shiftCard = (type: TSwiperDecision) => {
		swiperState.value.status = STATUS_CONSTANTS.LEAVING;
		swiperState.value.result = type;
		const activeItem = queue.value[0];
		// For super and down swipes, don't remove the card from the queue
		// Just emit the submit event and set state to revert (will be reset by reverted event)
		if (type === "super" || type === "down") {
			submitDecide(type, activeItem);
			// Set state to REVERT to trigger animation back to original position
			// The reverted event will reset the state to NORMAL when animation completes
			swiperState.value.status = STATUS_CONSTANTS.REVERT;
			return;
		}
		// Store the removed item for potential rewind (for non-super swipes)
		if (activeItem) {
			removedItems.push({ ...activeItem });
			// Keep only the last few items to prevent memory issues
			if (removedItems.length > 10) {
				removedItems.shift();
			}
		}
		queue.value = queue.value.slice(1, queue.value.length);
		submitDecide(type, activeItem);
	};
	const submitDecide = (type: TSwiperDecision, item: ISwiperContent) => {
		emit("submit", { type, key: item.swipeBetId, item });
	};
	const decide = (type: TSwiperDecision) => {
		if (swiperState.value.touchId) {
			return;
		}
		swiperState.value.status = STATUS_CONSTANTS.MOVING;
		swiperState.value.start = { x: 0, y: 0 };
		swiperState.value.move = {
			x: type === "super" || type === "down" ? 0 : type === "like" ? 160 : -160,
			y: type === "super" ? -1 : type === "down" ? 1 : 0,
		};

		requestAnimationFrame(() => {
			shiftCard(type);
		});

		swiperState.value.startPoint = 1;
	};
	const rewind = () => {
		if (swiperState.value.touchId || swiperState.value.status !== STATUS_CONSTANTS.NORMAL) {
			return false;
		}
		const lastRemovedItem = removedItems.pop();
		if (!lastRemovedItem) {
			return false;
		}
		// Create a new key for the rewinded item to trigger vue transitions
		const rewindKey = `${lastRemovedItem.swipeBetId}_rewind_${Date.now()}`;
		const rewindItem = { ...lastRemovedItem, $vtKey: rewindKey };
		// Add to rewind keys for animation - this is important for the beforeEnter animation
		swiperConfig.value.rewindKeys.push(rewindKey);
		// Add the item back to the beginning of the queue
		// The diff function will handle setting the REWINDING status
		queue.value = [rewindItem, ...queue.value];
		return true;
	};
	return {
		decide,
		shiftCard,
		rewind,
	};
};
export default useSwiperCore;
